import { useDispatch, useSelector } from 'react-redux';

import { countPdfPages } from 'helpers/countPdfPages';
import { useLocaleNavigate } from 'hooks/useLocaleNavigate';
import cloneDeep from 'lodash/cloneDeep';

import { Analytics } from 'services/analytics';

import { setConvertDocumentData, setPDFfileContent, updateDownloadProgress } from 'data/actions/documents';
import { toggleModal } from 'data/actions/modals';
import { userEmailSelector } from 'data/selectors/user';

import { blobToBase64 } from 'utils/blobToBase64';
import { formatFileSize } from 'utils/formatFileSize';

import { LOCAL_STORAGE_KEYS } from 'ts/constants/local-storage-keys';
import { PAGE_LINKS } from 'ts/constants/page-links';
import { EModalTypes } from 'ts/enums/modal.types';
import type { IConvertDocumentDto, IEditDocumentDto } from 'ts/interfaces/documents/document';
import type { IService } from 'ts/interfaces/services/service';
import { getFileTypeByName, InternalFileType } from 'ts/types/file';

const useFileUploadAndConvertEditorFlow = ({ service, modalType }: { service?: IService; modalType?: EModalTypes }) => {
  const navigate = useLocaleNavigate();
  const dispatch = useDispatch();
  const email = useSelector(userEmailSelector);

  const analyticsEventUpload = ({
    success,
    size,
    errorCode,
    file_counter,
    pages_counter
  }: {
    success: boolean;
    size: number;
    errorCode?: number;
    file_counter?: number;
    pages_counter?: number;
  }) => {
    void Analytics.sendEvent({
      event: 'file_upload_status',
      data: {
        status: success ? 'success' : 'fail',
        place: 'additional',
        errorCode,
        size: formatFileSize(size),
        accurate_size: size / 1000000,
        file_counter: file_counter || 1,
        file_format: '.pdf',
        is_validation_error: success ? 'false' : 'true',
        pages_counter: pages_counter || 1
      }
    });
  };

  const handleEditDocument = (dataToEdit: IEditDocumentDto, signUp?: boolean) => {
    return setTimeout(
      async () => {
        const dataToEditFileCopy = cloneDeep(dataToEdit);

        delete dataToEditFileCopy?.fileBase64;

        try {
          // set converted data to localStorage and use this data for google auth
          localStorage.setItem(
            LOCAL_STORAGE_KEYS.DATA_TO_EDIT,
            JSON.stringify({
              file: dataToEditFileCopy,
              service: service?.path,
              email: email,
              documentId: dataToEdit.documentId,
              fileBase64: dataToEdit.fileBase64
            })
          );
          // set converted data to localStorage and use this data for google auth
          localStorage.setItem(
            LOCAL_STORAGE_KEYS.DATA_TO_CONVERT,
            JSON.stringify({
              file: dataToEditFileCopy,
              service: service?.path,
              email: email,
              serviceType: service?.serviceType,
              fileBase64: dataToEdit.fileBase64
            })
          );
        } catch (e) {
          console.log('Error', e);
          //Commited for test
          // set converted data to localStorage and use this data for google auth
          localStorage.setItem(
            LOCAL_STORAGE_KEYS.DATA_TO_EDIT,
            JSON.stringify({
              file: dataToEditFileCopy,
              service: service?.path,
              email: email,
              documentId: dataToEdit.documentId,
              fileBase64: undefined
            })
          );
          // set converted data to localStorage and use this data for google auth
          localStorage.setItem(
            LOCAL_STORAGE_KEYS.DATA_TO_CONVERT,
            JSON.stringify({
              file: dataToEditFileCopy,
              service: service?.path,
              email: email,
              serviceType: service?.serviceType,
              fileBase64: undefined
            })
          );
        }

        dispatch(updateDownloadProgress(100));

        setTimeout(() => {
          if (!dataToEdit.fileBase64) {
            return;
          }

          dispatch(setPDFfileContent({ document: dataToEdit.fileBase64 }));
          dispatch(toggleModal({ visible: false }));
          navigate(`${PAGE_LINKS.EDITOR}?to=${service?.to}`);
          window.scrollTo(0, 0); // Scrolls to the top of the page
        }, 1000);
      },
      signUp ? 0 : 1000
    );
  };

  const handleEditFile = (dataToEdit: IEditDocumentDto) => {
    handleEditDocument(dataToEdit);
  };

  const handleSaveFileToLocalStorage = async (file: File, optionalFileParams: Record<string, any> = {}) => {
    try {
      const getModalType = () => {
        if (modalType) return modalType;
        return EModalTypes.PROGRESS_EDIT_FILE;
      };

      dispatch(updateDownloadProgress(0));
      dispatch(
        toggleModal({
          type: getModalType(),
          visible: true,
          options: { file }
        })
      );

      const from: InternalFileType =
        getFileTypeByName(file?.name) ||
        (Array.isArray(service?.from) ? service?.from[0] : service?.from) ||
        InternalFileType.PDF;

      const pagesCount = from === InternalFileType.PDF ? await countPdfPages(file) : 1;

      const mergedFilesCount = optionalFileParams.mergedFilesCount;

      if (typeof mergedFilesCount === 'number') {
        localStorage.setItem(LOCAL_STORAGE_KEYS.MERGED_FILES_COUNT, mergedFilesCount.toString());
      }

      const fileBase64 = await blobToBase64(file);

      const dataToEdit: IEditDocumentDto = {
        filename: file.name,
        size: file.size,
        key: null,
        pagesCount,
        serviceType: service?.serviceType,
        fileBase64,
        ...optionalFileParams
      };

      const dataToConvert: IConvertDocumentDto = {
        filename: file.name,
        size: file.size,
        key: null,
        pagesCount,
        from,
        to: (Array.isArray(service?.to) ? service?.to[0] : service?.to) || InternalFileType.PNG,
        serviceType: service?.serviceType,
        fileBase64,
        ...optionalFileParams
      };

      // awaiting the downloading animation
      setTimeout(() => {
        dispatch(setConvertDocumentData(dataToConvert));

        handleEditFile(dataToEdit);
        analyticsEventUpload({ size: file?.size, success: true, pages_counter: pagesCount });
      }, 5000);
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  // upload to s3 bucket and convert file
  const handleUploadFile = (file: File, optionalFileParams: Record<string, any> = {}) => {
    if (!file) return;

    void Analytics.sendEvent({
      event: 'file_from_provider_chosen',
      data: {
        features_name: service?.path?.replace('/', '') || '',
        method: 'click'
      }
    });

    handleSaveFileToLocalStorage(file, optionalFileParams);
  };

  return {
    handleUploadFile,
    handleConvertDocument: handleEditDocument
  };
};

export default useFileUploadAndConvertEditorFlow;
